<template>
  <section class="news-page page-stack">
    <article class="card page-hero-card news-hero">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Warcraft news</p>
          <h2>News</h2>
          <p>Official blue posts, Wowhead, and WoW Hub in one focused Warcraft reader.</p>
        </div>
      </header>
    </article>

    <div ref="newsToolbar" class="card filter-toolbar news-toolbar" :class="{ 'is-native-feed': isNativeLanguageFeed }">
      <div class="applied-filters">
        <span class="filter-pill">Posts: <span>{{ totalPostCount }}</span></span>
        <span class="filter-pill">Feed: <span>{{ activeNavigationLabel }}</span></span>
        <span v-if="totalPages > 1" class="filter-pill">Page: <span>{{ currentPage }} / {{ totalPages }}</span></span>
        <span v-if="lastUpdatedLabel" class="filter-pill">Updated: <span>{{ lastUpdatedLabel }}</span></span>
        <span v-if="search" class="filter-pill">Search: <span>{{ search }}</span></span>
      </div>
      <div class="news-search-row">
        <div role="group" class="quick-search-field">
          <input
            v-model.trim="search"
            class="input"
            :aria-label="`Search ${activeFeed.label}`"
            :placeholder="`Search ${activeFeed.label}`"
            type="search"
          />
        </div>
        <button
          class="btn-icon-outline news-search-refresh"
          type="button"
          aria-label="Refresh news"
          title="Refresh news"
          :disabled="loading"
          @click="loadPosts(true)"
        >
          <i class="fa-solid fa-rotate"></i>
        </button>
      </div>
      <div class="page-actions">
        <div v-if="!isNativeLanguageFeed" class="language-dropdown" :class="{ 'is-open': languageMenuOpen }" aria-label="News language">
          <button
            class="btn-sm-outline language-dropdown-trigger"
            type="button"
            :disabled="loading"
            aria-haspopup="menu"
            :aria-expanded="languageMenuOpen"
            @click.stop="toggleLanguageMenu"
          >
            <i class="fa-solid fa-language"></i>
            {{ activeLanguageLabel }}
            <i class="fa-solid fa-chevron-down"></i>
          </button>
          <Transition name="favorite-menu">
            <div v-if="languageMenuOpen" class="language-dropdown-menu" role="menu" @click.stop>
              <button
                v-for="language in languages"
                :key="language.value"
                type="button"
                role="menuitem"
                :class="{ 'is-active': activeLanguage === language.value }"
                @click="selectLanguage(language.value)"
              >
                <span>{{ language.name }}</span>
                <i v-if="activeLanguage === language.value" class="fa-solid fa-check"></i>
              </button>
            </div>
          </Transition>
        </div>
        <span v-if="!isNativeLanguageFeed" class="translation-provider-chip">
          <i class="fa-solid fa-bolt"></i>
          {{ translationProviderLabel }}
        </span>
        <div class="view-switch news-layout-switch" aria-label="News layout">
          <button
            class="btn-sm-outline"
            :class="{ 'is-active': viewMode === 'grid' }"
            type="button"
            aria-label="Grid layout"
            title="Grid layout"
            @click="viewMode = 'grid'"
          >
            <i class="fa-solid fa-grip"></i>
          </button>
          <button
            class="btn-sm-outline"
            :class="{ 'is-active': viewMode === 'list' }"
            type="button"
            aria-label="List layout"
            title="List layout"
            @click="viewMode = 'list'"
          >
            <i class="fa-solid fa-list"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="card news-taxonomy-nav" aria-label="News source and category">
      <div class="news-source-tabs" role="tablist" aria-label="News sources">
        <button
          v-for="source in sourceTabs"
          :key="source.key"
          class="news-source-tab"
          :class="{ 'is-active': activeSource === source.key }"
          type="button"
          role="tab"
          :aria-selected="activeSource === source.key"
          @click="selectSource(source.key)"
        >
          <img class="news-source-tab-logo" :src="source.logo" alt="" aria-hidden="true">
          <span>{{ source.label }}</span>
        </button>
      </div>

      <div class="news-category-dropdown" :class="{ 'is-open': categoryMenuOpen }" @click.stop>
        <button
          class="news-category-trigger"
          type="button"
          :disabled="loading"
          aria-haspopup="menu"
          :aria-expanded="categoryMenuOpen"
          @click="toggleCategoryMenu"
        >
          <span class="news-category-trigger-copy">
            <small>Category</small>
            <strong>{{ activeCategoryLabel }}</strong>
          </span>
          <i class="fa-solid fa-chevron-down" aria-hidden="true"></i>
        </button>

        <Transition name="favorite-menu">
          <div v-if="categoryMenuOpen" class="news-category-menu" role="menu">
            <button
              v-for="category in activeCategoryOptions"
              :key="category.key"
              type="button"
              role="menuitemradio"
              :aria-checked="activeCategoryKey === category.key"
              :class="{ 'is-active': activeCategoryKey === category.key }"
              @click="selectCategory(category.key)"
            >
              <span>{{ category.label }}</span>
              <small v-if="Number.isFinite(category.count)">{{ category.count }}</small>
              <i v-if="activeCategoryKey === category.key" class="fa-solid fa-check" aria-hidden="true"></i>
            </button>
          </div>
        </Transition>
      </div>
    </div>

    <div v-if="isNativeLanguageFeed" class="card source-language-note" role="status">
      <span><strong>WoW Hub publishes in Ukrainian.</strong> These posts are shown in their original language and are not translated.</span>
    </div>

    <div v-if="loading" class="news-grid" :class="{ 'is-list-view': viewMode === 'list' }">
      <article v-for="index in 6" :key="index" class="card news-card news-skeleton">
        <span class="skeleton-media news-banner-skeleton"></span>
        <span class="skeleton-line skeleton-line-short"></span>
        <span class="skeleton-line"></span>
        <span class="skeleton-line skeleton-line-medium"></span>
      </article>
    </div>

    <AppEmptyState
      v-else-if="loadError"
      eyebrow="News"
      icon="fa-solid fa-triangle-exclamation"
      title="Unable to load the feed"
      :message="loadError"
    />

    <AppEmptyState
      v-else-if="!currentPageSourcePosts.length"
      eyebrow="No matches"
      icon="fa-solid fa-magnifying-glass"
      title="No posts found"
      message="Try a different search phrase, switch feed source, or refresh the feed."
    />

    <template v-else>
      <div class="news-grid" :class="{ 'is-list-view': viewMode === 'list' }">
        <article
          v-for="post in posts"
          :key="post.id"
          class="card news-card"
          :class="{ 'is-news-card': post.kind === 'news', 'is-blue-card': post.kind === 'blue', 'has-banner': post.kind !== 'blue' }"
        >
          <div class="wowhead-card-banner" :class="{ 'is-blue-banner': post.kind === 'blue', 'is-empty-banner': !post.imageUrl && post.kind !== 'blue' }">
            <RouterLink v-if="post.imageUrl" class="wowhead-news-banner" :to="articleRoute(post)">
              <img :src="post.imageUrl" :alt="post.title" loading="lazy" @error="handleCardImageError(post)">
            </RouterLink>
            <div v-else-if="post.kind === 'blue'" class="wowhead-blue-banner" aria-hidden="true">
              <i class="fa-brands fa-battle-net"></i>
            </div>
            <RouterLink v-else class="wowhead-news-banner wowhead-news-banner-fallback" :to="articleRoute(post)">
              <i class="fa-solid fa-newspaper"></i>
              <span>{{ post.sourceLabel || 'News' }}</span>
              <strong>{{ post.title }}</strong>
            </RouterLink>
            <span class="wowhead-category-badge">{{ post.category }}</span>
          </div>

          <header class="news-card-head">
            <div>
              <h3>
                <RouterLink :to="articleRoute(post)">{{ post.title }}</RouterLink>
              </h3>
            </div>
          </header>

          <p class="news-card-summary">{{ post.summary || `Open the ${post.sourceLabel || 'source'} post to read the full update.` }}</p>

          <footer class="news-card-foot">
            <time v-if="post.publishedAt" :datetime="post.publishedAt">{{ formatDate(post.publishedAt) }}</time>
            <span v-else>Recent update</span>
            <RouterLink class="ranking-link" :to="articleRoute(post)">
              Read post
              <i class="fa-solid fa-arrow-right"></i>
            </RouterLink>
          </footer>
        </article>
      </div>

      <AppPagination
        v-if="totalPages > 1"
        :page="currentPage"
        :total-pages="totalPages"
        aria-label="News pagination"
        page-text="Page"
        @change="changePage"
      />
    </template>
  </section>
</template>

<script>
import AppEmptyState from '../components/AppEmptyState.vue'
import AppPagination from '../components/AppPagination.vue'
import { WOWHEAD_NEWS_FEEDS, getFeedFallbackStatus, getWowheadArticleImage, getWowheadFeedPostsRaw } from '../services/wowheadNews'
import { getLastTranslationProvider, translatePosts } from '../services/translation'
import wowheadLogo from '../assets/images/services/wowhead-logo.png'
import wowhubLogo from '../assets/images/services/wowhub-logo.png'

const NEWS_STATE_KEY = 'rio-dashboard-news-state'
const NEWS_LANGUAGE_KEY = 'rio-news-language'
const NEWS_RETURN_STATE_KEY = 'rio-news-return-state'
const NEWS_PAGE_SIZE = 24
const NEWS_LIST_SCROLL_OFFSET = 16
const NEWS_FEED_KEYS = ['all', 'blue', 'retail', 'classic', 'wowhub']
// Blue Tracker is a Wowhead feed, so it sits with the other Wowhead categories rather than
// competing with WoW Hub for a top-level source tab.
const WOWHEAD_CATEGORY_KEYS = ['all', 'retail', 'classic', 'blue']
const ALL_SOURCE_CATEGORY_KEY = 'all'
const DEFAULT_NEWS_STATE = {
  viewMode: 'grid',
  activeLanguage: 'en',
  activeFeedKey: 'all',
  sourceCategories: {
    wowhead: 'all',
    wowhub: 'all',
  },
}

function normalizeFeedKey(value) {
  const feedKey = Array.isArray(value) ? value[0] : value
  return NEWS_FEED_KEYS.includes(feedKey) ? feedKey : null
}

function routeValue(value) {
  return Array.isArray(value) ? value[0] : value
}

function sourceFromFeedKey(feedKey) {
  return feedKey === 'wowhub' ? 'wowhub' : 'wowhead'
}

function normalizeSourceCategory(value) {
  return String(routeValue(value) || '')
    .trim()
    .toLocaleLowerCase('uk-UA')
    .replace(/\s+/g, ' ')
}

function resolveRouteCategory(query = {}, feedKey = 'all') {
  if (sourceFromFeedKey(feedKey) === 'wowhead') {
    return WOWHEAD_CATEGORY_KEYS.includes(feedKey) ? feedKey : ALL_SOURCE_CATEGORY_KEY
  }

  return normalizeSourceCategory(query.category) || ALL_SOURCE_CATEGORY_KEY
}

function resolveRouteFeedKey(query = {}) {
  const source = routeValue(query.source)

  if (source === 'wowhead') {
    const category = normalizeFeedKey(query.category)
    return WOWHEAD_CATEGORY_KEYS.includes(category) ? category : 'all'
  }
  // Blue Tracker used to be its own source; links shared back then still resolve to its feed.
  if (source === 'blue') return 'blue'
  if (source === 'wowhub') return 'wowhub'

  // Keep links shared before the source/category navigation update working.
  return normalizeFeedKey(query.feed)
}

function loadNewsState() {
  try {
    const saved = JSON.parse(localStorage.getItem(NEWS_STATE_KEY) || '{}')
    return {
      viewMode: ['grid', 'list'].includes(saved.viewMode) ? saved.viewMode : DEFAULT_NEWS_STATE.viewMode,
      activeLanguage: ['en', 'uk', 'ru'].includes(localStorage.getItem(NEWS_LANGUAGE_KEY)) ? localStorage.getItem(NEWS_LANGUAGE_KEY) : DEFAULT_NEWS_STATE.activeLanguage,
      activeFeedKey: normalizeFeedKey(saved.activeFeedKey) || DEFAULT_NEWS_STATE.activeFeedKey,
      sourceCategories: {
        ...DEFAULT_NEWS_STATE.sourceCategories,
        ...(saved.sourceCategories || {}),
      },
    }
  } catch (error) {
    return { ...DEFAULT_NEWS_STATE }
  }
}

function loadNewsReturnState() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(NEWS_RETURN_STATE_KEY) || 'null')
    if (!saved || !normalizeFeedKey(saved.activeFeedKey)) return null

    return {
      activeFeedKey: saved.activeFeedKey,
      currentPage: Math.max(1, Number(saved.currentPage) || 1),
      search: String(saved.search || ''),
      scrollTop: Math.max(0, Number(saved.scrollTop) || 0),
      activeCategoryKey: normalizeSourceCategory(saved.activeCategoryKey) || ALL_SOURCE_CATEGORY_KEY,
    }
  } catch {
    return null
  }
}

export default {
  name: 'NewsView',
  components: {
    AppEmptyState,
    AppPagination,
  },
  data() {
    const savedState = loadNewsState()
    const returnState = loadNewsReturnState()
    const sharedFeedKey = resolveRouteFeedKey(this.$route.query)
    const activeFeedKey = sharedFeedKey || returnState?.activeFeedKey || savedState.activeFeedKey
    const activeSource = sourceFromFeedKey(activeFeedKey)
    const sourceCategories = {
      ...DEFAULT_NEWS_STATE.sourceCategories,
      ...savedState.sourceCategories,
      wowhead: WOWHEAD_CATEGORY_KEYS.includes(activeFeedKey) ? activeFeedKey : 'all',
    }

    if (sharedFeedKey) sourceCategories[activeSource] = resolveRouteCategory(this.$route.query, activeFeedKey)
    else if (returnState?.activeCategoryKey) sourceCategories[activeSource] = returnState.activeCategoryKey

    return {
      posts: [],
      rawPosts: [],
      search: returnState?.search || '',
      loading: false,
      loadError: '',
      lastUpdatedAt: '',
      activeFeedKey,
      sourceCategories,
      currentPage: returnState?.currentPage || 1,
      pendingScrollTop: returnState?.scrollTop ?? null,
      pageSize: NEWS_PAGE_SIZE,
      viewMode: savedState.viewMode,
      activeLanguage: savedState.activeLanguage,
      languageMenuOpen: false,
      categoryMenuOpen: false,
      translationInProgress: false,
      translationProvider: getLastTranslationProvider(),
      languages: [
        { value: 'en', label: 'EN', name: 'English' },
        { value: 'uk', label: 'UA', name: 'Українська' },
        { value: 'ru', label: 'RU', name: 'Русский' },
      ],
      feeds: WOWHEAD_NEWS_FEEDS,
    }
  },
  computed: {
    activeSource() {
      return sourceFromFeedKey(this.activeFeedKey)
    },
    wowheadCategory() {
      return WOWHEAD_CATEGORY_KEYS.includes(this.activeFeedKey) ? this.activeFeedKey : 'all'
    },
    sourceTabs() {
      return [
        { key: 'wowhead', label: 'Wowhead', logo: wowheadLogo },
        { key: 'wowhub', label: 'WoW Hub', logo: wowhubLogo },
      ]
    },
    wowheadCategories() {
      return [
        { key: 'all', label: 'All News' },
        { key: 'retail', label: 'Retail WoW' },
        { key: 'classic', label: 'Classic WoW' },
        { key: 'blue', label: 'Blue Tracker' },
      ]
    },
    activeSourceTab() {
      return this.sourceTabs.find((source) => source.key === this.activeSource) || this.sourceTabs[0]
    },
    activeCategoryKey() {
      return this.activeSource === 'wowhead'
        ? this.wowheadCategory
        : normalizeSourceCategory(this.sourceCategories[this.activeSource]) || ALL_SOURCE_CATEGORY_KEY
    },
    activeCategoryOptions() {
      if (this.activeSource === 'wowhead') {
        return this.wowheadCategories.map((category) => ({
          ...category,
          count: category.key === this.wowheadCategory ? this.rawPosts.length : null,
        }))
      }

      const categories = new Map()
      this.rawPosts.forEach((post) => {
        const label = String(post.category || '').trim()
        const key = normalizeSourceCategory(label)
        if (!key) return
        const current = categories.get(key)
        categories.set(key, { key, label: current?.label || label, count: (current?.count || 0) + 1 })
      })

      return [
        { key: ALL_SOURCE_CATEGORY_KEY, label: 'All categories', count: this.rawPosts.length },
        ...[...categories.values()].sort((left, right) => left.label.localeCompare(right.label, 'uk')),
      ]
    },
    activeCategoryLabel() {
      return this.activeCategoryOptions.find((category) => category.key === this.activeCategoryKey)?.label
        || (this.activeCategoryKey === ALL_SOURCE_CATEGORY_KEY ? 'All categories' : this.activeCategoryKey)
    },
    activeNavigationLabel() {
      return `${this.activeSourceTab.label} · ${this.activeCategoryLabel}`
    },
    activeFeed() {
      return this.feeds.find((feed) => feed.key === this.activeFeedKey) || this.feeds[0]
    },
    rssUrl() {
      return this.activeFeed.url
    },
    // The translation providers only translate from English, so a feed that publishes in its own
    // language is shown as-is and hides the language picker.
    isNativeLanguageFeed() {
      return (this.activeFeed.nativeLanguage || 'en') !== 'en'
    },
    filteredRawPosts() {
      const categoryPosts = this.activeCategoryKey === ALL_SOURCE_CATEGORY_KEY || this.activeSource === 'wowhead'
        ? this.rawPosts
        : this.rawPosts.filter((post) => normalizeSourceCategory(post.category) === this.activeCategoryKey)
      const query = this.search.toLowerCase()
      if (!query) return categoryPosts

      return categoryPosts.filter((post) => {
        return [post.title, post.summary, post.category].some((value) => String(value || '').toLowerCase().includes(query))
      })
    },
    totalPostCount() {
      return this.filteredRawPosts.length
    },
    totalPages() {
      return Math.max(Math.ceil(this.totalPostCount / this.pageSize), 1)
    },
    currentPageSourcePosts() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredRawPosts.slice(start, start + this.pageSize)
    },
    lastUpdatedLabel() {
      return this.lastUpdatedAt ? this.formatTime(this.lastUpdatedAt) : ''
    },
    activeLanguageLabel() {
      return this.languages.find((language) => language.value === this.activeLanguage)?.label || 'EN'
    },
    translationProviderLabel() {
      if (this.activeLanguage === 'en') return 'No translate'
      if (this.translationInProgress) return 'In process...'
      return this.formatTranslationProvider(this.translationProvider)
    },
  },
  async mounted() {
    document.addEventListener('click', this.closeLanguageMenu)
    window.addEventListener('rio:translation-provider', this.updateTranslationProvider)
    this.syncFeedQuery()
    await this.loadPosts()
    this.restoreReturnPosition()
  },
  beforeRouteLeave(to) {
    if (to.name === 'news-article') this.saveReturnPosition()
    else sessionStorage.removeItem(NEWS_RETURN_STATE_KEY)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeLanguageMenu)
    window.removeEventListener('rio:translation-provider', this.updateTranslationProvider)
  },
  methods: {
    updateTranslationProvider(event) {
      this.translationProvider = event.detail || null
    },
    formatTranslationProvider(provider) {
      if (provider?.status === 'limit') return 'Limit reached'

      const label = provider?.label || 'Unknown provider'
      const queue = provider?.index && provider?.total ? ` - ${provider.index}/${provider.total}` : ''
      const cache = provider?.cached ? 'Cache - ' : ''
      return `${cache}${label}${queue}`
    },
    finalizeTranslationProvider() {
      if (this.activeLanguage === 'en') return

      const provider = getLastTranslationProvider()
      this.translationProvider = provider?.status && provider.status !== 'none'
        ? provider
        : {
          key: 'feed-cache',
          label: 'Feed cache',
          cached: true,
          status: 'cached',
        }
    },
    closeLanguageMenu() {
      this.languageMenuOpen = false
      this.categoryMenuOpen = false
    },
    toggleLanguageMenu() {
      this.categoryMenuOpen = false
      this.languageMenuOpen = !this.languageMenuOpen
    },
    toggleCategoryMenu() {
      this.languageMenuOpen = false
      this.categoryMenuOpen = !this.categoryMenuOpen
    },
    selectLanguage(language) {
      this.activeLanguage = language
      this.languageMenuOpen = false
    },
    selectFeed(feedKey) {
      if (this.activeFeedKey === feedKey || this.loading) return
      this.scrollToTop()
      this.activeFeedKey = feedKey
      if (WOWHEAD_CATEGORY_KEYS.includes(feedKey)) {
        this.setSourceCategory('wowhead', feedKey)
      }
      this.search = ''
      this.currentPage = 1
      this.persistNewsState()
      this.syncFeedQuery()
      this.loadPosts()
    },
    selectSource(source) {
      this.categoryMenuOpen = false
      const feedKey = source === 'wowhub'
        ? 'wowhub'
        : WOWHEAD_CATEGORY_KEYS.includes(this.sourceCategories.wowhead)
          ? this.sourceCategories.wowhead
          : 'all'

      this.selectFeed(feedKey)
    },
    setSourceCategory(source, categoryKey) {
      this.sourceCategories = {
        ...this.sourceCategories,
        [source]: normalizeSourceCategory(categoryKey) || ALL_SOURCE_CATEGORY_KEY,
      }
    },
    selectCategory(categoryKey) {
      const normalizedCategory = normalizeSourceCategory(categoryKey) || ALL_SOURCE_CATEGORY_KEY
      this.categoryMenuOpen = false
      if (normalizedCategory === this.activeCategoryKey || this.loading) return

      if (this.activeSource === 'wowhead') {
        this.setSourceCategory('wowhead', normalizedCategory)
        this.selectFeed(normalizedCategory)
        return
      }

      this.scrollToTop()
      this.setSourceCategory(this.activeSource, normalizedCategory)
      this.currentPage = 1
      this.persistNewsState()
      this.syncFeedQuery()
      this.loadCurrentPage()
    },
    ensureActiveCategory() {
      if (this.activeCategoryOptions.some((category) => category.key === this.activeCategoryKey)) return
      this.setSourceCategory(this.activeSource, ALL_SOURCE_CATEGORY_KEY)
      this.currentPage = 1
      this.persistNewsState()
      this.syncFeedQuery()
    },
    syncFeedQuery() {
      const { feed, source, category, ...otherQuery } = this.$route.query
      const feedQuery = {
        source: this.activeSource,
        category: this.activeCategoryKey,
      }

      const hasCanonicalQuery = !feed
        && source === feedQuery.source
        && category === feedQuery.category
      if (hasCanonicalQuery) return

      this.$router.replace({
        name: 'news',
        query: {
          ...otherQuery,
          ...feedQuery,
        },
      })
    },
    changePage(page) {
      if (this.loading || page === this.currentPage) return
      this.currentPage = page
      // Banner enrichment resolves after the new page renders and changes card heights, so the
      // position is re-asserted once the page has settled rather than left where it landed.
      this.loadCurrentPage().finally(() => {
        this.$nextTick(() => this.scrollToTop())
      })
    },
    currentScrollTop() {
      return Math.max(
        Number(window.scrollY) || 0,
        Number(document.documentElement?.scrollTop) || 0,
        Number(document.querySelector('.app-global-wrapper')?.scrollTop) || 0,
      )
    },
    scrollToPosition(top) {
      const position = Math.max(0, Number(top) || 0)
      window.scrollTo({ top: position, behavior: 'auto' })
      document.querySelector('.app-global-wrapper')?.scrollTo({ top: position, behavior: 'auto' })
    },
    scrollToTop() {
      this.scrollToPosition(0)
    },
    // Paging keeps the toolbar in view instead of the hero, so the first card of the new page
    // sits right below the counters.
    scrollToListTop() {
      const anchor = this.$refs.newsToolbar
      if (!anchor) {
        this.scrollToTop()
        return
      }

      this.scrollToPosition(anchor.getBoundingClientRect().top + this.currentScrollTop() - NEWS_LIST_SCROLL_OFFSET)
    },
    saveReturnPosition() {
      try {
        sessionStorage.setItem(NEWS_RETURN_STATE_KEY, JSON.stringify({
          activeFeedKey: this.activeFeedKey,
          currentPage: this.currentPage,
          search: this.search,
          scrollTop: this.currentScrollTop(),
          activeCategoryKey: this.activeCategoryKey,
        }))
      } catch (error) {
        console.error('Unable to save News return position', error)
      }
    },
    restoreReturnPosition() {
      if (this.pendingScrollTop === null) return

      const position = this.pendingScrollTop
      this.pendingScrollTop = null
      sessionStorage.removeItem(NEWS_RETURN_STATE_KEY)

      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => this.scrollToPosition(position))
        })
      })
    },
    articleRoute(post) {
      return {
        name: 'news-article',
        query: {
          url: post.link,
          source: post.source || 'wowhead',
        },
      }
    },
    async loadPosts(force = false) {
      force = force === true
      this.loading = true
      this.loadError = ''
      const feedLoadingId = await this.$store.dispatch('startGlobalLoading', `Loading ${this.activeFeed.label}...`)
      const cacheContext = {}

      try {
        this.rawPosts = await getWowheadFeedPostsRaw(this.activeFeedKey, { force, cacheContext })
        this.ensureActiveCategory()
        this.currentPage = Math.min(this.currentPage, this.totalPages)
        await this.loadCurrentPage(false)
        const fallback = getFeedFallbackStatus(this.activeFeedKey)
        this.lastUpdatedAt = fallback?.timestamp
          ? new Date(fallback.timestamp).toISOString()
          : new Date(cacheContext.updatedAt || Date.now()).toISOString()

        if (cacheContext.source === 'fallback' || (fallback && cacheContext.source !== 'cache')) {
          this.$store.dispatch('notify', {
            type: 'warning',
            title: `Saved ${this.activeFeed.label} feed`,
            description: 'The live news request failed, so the most recent saved posts are shown.',
          })
        } else if (cacheContext.source !== 'cache' && !this.loadError) {
          this.$store.dispatch('notify', {
            type: 'success',
            title: force ? 'News refreshed' : 'News updated',
            description: `The latest ${this.activeFeed.label} posts are now displayed.`,
          })
        }
      } catch (error) {
        this.loadError = `${this.activeFeed.label} is unavailable right now. Try refreshing the news or opening the source directly.`
        this.$store.dispatch('notify', {
          type: 'danger',
          title: `${this.activeFeed.label} unavailable`,
          description: this.loadError,
        })
      } finally {
        this.finalizeTranslationProvider()
        this.$store.dispatch('finishGlobalLoading', feedLoadingId)
        this.loading = false
      }
    },
    async loadCurrentPage(ownLoading = true) {
      if (ownLoading) {
        this.loading = true
        this.loadError = ''
      }

      const pagePosts = this.currentPageSourcePosts
      let posts = pagePosts
      let translationLoadingId = null

      try {
        if (!this.isNativeLanguageFeed && this.activeLanguage !== 'en' && pagePosts.length) {
          translationLoadingId = await this.$store.dispatch('startGlobalLoading', `Translating ${this.activeFeed.label} page ${this.currentPage} to ${this.activeLanguageLabel}...`)
          this.translationInProgress = true
          posts = await translatePosts(pagePosts, this.activeLanguage)
          this.finalizeTranslationProvider()
        }

        this.posts = this.activeFeed.kind === 'blue' ? posts : await this.enrichNewsBanners(posts)
      } catch (error) {
        this.loadError = 'Unable to prepare this news page right now. Try refreshing the feed.'
        this.$store.dispatch('notify', {
          type: 'danger',
          title: `${this.activeFeed.label} page unavailable`,
          description: this.loadError,
        })
      } finally {
        if (translationLoadingId) {
          this.$store.dispatch('finishGlobalLoading', translationLoadingId)
        }
        this.translationInProgress = false
        if (ownLoading) this.loading = false
      }
    },
    async enrichNewsBanners(posts) {
      const bannerLimit = 12
      const enrichedPosts = [...posts]
      const targets = enrichedPosts.filter((post) => ['news', 'wowhub'].includes(post.kind)).slice(0, bannerLimit)
      const results = await Promise.allSettled(
        targets.map(async (post) => {
          if (post.imageUrl || !post.link) return post
          return {
            ...post,
            imageUrl: await getWowheadArticleImage(post.link, post.source),
          }
        })
      )

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const postIndex = enrichedPosts.findIndex((post) => post.id === result.value.id)
          if (postIndex !== -1) {
            enrichedPosts[postIndex] = result.value
          }
        }
      })

      return enrichedPosts
    },
    handleCardImageError(post) {
      post.imageUrl = ''
    },
    formatDate(value) {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    },
    formatTime(value) {
      return new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    },
    persistNewsState() {
      try {
        localStorage.setItem(NEWS_STATE_KEY, JSON.stringify({
          viewMode: this.viewMode,
          activeFeedKey: this.activeFeedKey,
          sourceCategories: this.sourceCategories,
        }))
        localStorage.setItem(NEWS_LANGUAGE_KEY, this.activeLanguage)
      } catch (error) {
        console.error('Unable to save News state', error)
      }
    },
  },
  watch: {
    '$route.fullPath'() {
      const requestedFeedKey = resolveRouteFeedKey(this.$route.query)
      if (!requestedFeedKey) return

      const requestedSource = sourceFromFeedKey(requestedFeedKey)
      const requestedCategory = resolveRouteCategory(this.$route.query, requestedFeedKey)

      if (requestedFeedKey === this.activeFeedKey) {
        if (requestedCategory === this.activeCategoryKey) return
        this.scrollToTop()
        this.setSourceCategory(requestedSource, requestedCategory)
        this.currentPage = 1
        this.persistNewsState()
        this.loadCurrentPage()
        return
      }

      this.scrollToTop()
      this.setSourceCategory(requestedSource, requestedCategory)
      this.activeFeedKey = requestedFeedKey
      this.search = ''
      this.currentPage = 1
      this.persistNewsState()
      this.loadPosts()
    },
    viewMode() {
      this.persistNewsState()
    },
    activeLanguage() {
      this.persistNewsState()
      this.loadCurrentPage()
    },
    search() {
      this.currentPage = 1
      this.loadCurrentPage()
    },
  },
}
</script>
